import React from "react";
import Footer from "../Footer";

const stats = [
  { label: "Verified tutor profiles", value: "Trusted" },
  { label: "Tutor-side application system", value: "Simple" },
  { label: "Guardian browsing access", value: "Free" },
];

const features = [
  {
    title: "Verified Tutors",
    description:
      "We focus on listing tutors with clear academic details, subject preferences, location information, and profile transparency so guardians can make better decisions.",
  },
  {
    title: "Smarter Tuition Matching",
    description:
      "Tutors can apply to suitable tuition posts, while guardians and students can browse tutor profiles directly from the Tutor Board.",
  },
  {
    title: "Clear Commission Policy",
    description:
      "Guardians are not charged commission. Tutors pay service commission only after a tuition is successfully confirmed through Tutor Network BD.",
  },
  {
    title: "Built for Bangladesh",
    description:
      "Tutor Network BD is designed around the local tuition market, including area-based search, salary expectations, tutor preferences, and guardian needs.",
  },
];

const steps = [
  {
    title: "Guardians post or browse",
    description:
      "Guardians and students can post tuition requirements or directly explore available tutor profiles.",
  },
  {
    title: "Tutors apply to tuitions",
    description:
      "Verified tutors can apply to tuition posts that match their subject, location, class level, and preference.",
  },
  {
    title: "Tuition gets confirmed",
    description:
      "After discussion between tutor and guardian, Tutor Network BD may check confirmation and support the process.",
  },
];

export default function About() {
  return (
    <>
      <main className="min-h-screen bg-slate-50 text-slate-900">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-white sm:px-6 lg:px-8">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-24 right-10 h-72 w-72 rounded-full bg-emerald-400 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-sky-400 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-6xl">
            <span className="inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-1.5 text-sm font-medium text-emerald-100">
              About Tutor Network BD
            </span>

            <h1 className="mt-5 max-w-4xl text-4xl font-bold tracking-tight md:text-6xl">
              A smarter way to connect tutors, students, and guardians in
              Bangladesh.
            </h1>

            <p className="mt-7 max-w-3xl text-base leading-8 text-slate-200 md:text-lg">
              Tutor Network BD is a private tutor marketplace under Goat
              Teachers, built to make tuition discovery easier, safer, and more
              transparent for everyone involved.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/tutor-board"
                className="rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-emerald-300"
              >
                Find Tutors
              </a>
              <a
                href="/tuition-board"
                className="rounded-full border border-white/15 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                View Tuitions
              </a>
            </div>
          </div>
        </section>

        <section className="px-4 py-10 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
            {stats.map((item) => (
              <div
                key={item.label}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
              >
                <p className="text-3xl font-bold text-slate-950">
                  {item.value}
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 pb-10 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
                Who we are
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                We are building a reliable tuition network for Bangladesh.
              </h2>
              <p className="mt-5 text-base leading-8 text-slate-600">
                Finding the right private tutor should not feel random or
                confusing. Tutor Network BD helps guardians discover tutors with
                clearer profiles, while tutors get a structured place to find
                tuition opportunities.
              </p>
              <p className="mt-4 text-base leading-8 text-slate-600">
                Our goal is to reduce uncertainty in the tuition market by
                improving tutor visibility, tuition post quality, application
                control, and communication between both sides.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-lg font-bold text-emerald-700">
                    ✓
                  </div>
                  <h3 className="text-lg font-semibold text-slate-950">
                    {feature.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-10 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
                How it works
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                Simple process. Clear roles. Better tuition decisions.
              </h2>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className="rounded-3xl bg-slate-50 p-6 ring-1 ring-slate-200"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-950 text-sm font-bold text-white">
                    {index + 1}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-slate-950">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-6xl overflow-hidden rounded-3xl bg-slate-950 p-8 text-white shadow-sm md:p-10">
            <div className="grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-center">
              <div>
                <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                  Our mission is to make private tuition more organized,
                  transparent, and trustworthy.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300">
                  We want tutors to find real opportunities and guardians to
                  find suitable tutors without unnecessary confusion, hidden
                  processes, or unreliable information.
                </p>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-6">
                <p className="text-sm text-slate-300">Operated by</p>
                <p className="mt-1 text-xl font-semibold">Goat Technologies</p>
                <div className="mt-5 space-y-2 text-sm leading-6 text-slate-300">
                  <p>Website: tn.goatteachers.com</p>
                  <p>Support: support@goatteachers.com</p>
                  <p>Phone/WhatsApp: +8801824796093</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
